import { PEPTIDE_LINKS } from "@/config/peptide-links";

export type PeptideGoal = "recovery" | "anti-aging" | "weight-management" | "skin" | "tanning" | "cognitive";

export interface PeptideProfile {
	slug: string;
	name: string;
	family: string;
	mechanism: string;
	receptorTargets: string[];
	categories: PeptideGoal[];
	timing: "morning" | "evening" | "flexible";
	halfLife: string;
}

export interface StackCombination {
	peptides: [string, string]; // sorted slugs (pairwise, alphabetically)
	synergyScore: 1 | 2 | 3 | 4 | 5;
	rationale: string;
	rationaleKey: string;
	timingAdvice: string;
	timingAdviceKey: string;
	overlapWarning?: string;
	overlapWarningKey?: string;
}

export interface GoalStack {
	goal: PeptideGoal;
	nameKey: string;
	descriptionKey: string;
	peptides: string[];
	icon: string;
}

export interface BlendMatch {
	individual: string[];
	blend: string;
}

export const MAX_STACK_SIZE = 4;

export const SYNERGY_LABELS: Record<number, string> = {
	5: "excellent",
	4: "veryGood",
	3: "moderate",
	2: "low",
	1: "notRecommended",
};

export const PEPTIDE_PROFILES: PeptideProfile[] = [
	{
		slug: "cagrilintide",
		name: "Cagrilintide",
		family: "Long-Acting Amylin Analogue",
		mechanism:
			"Investigational lipidated amylin analogue characterized in receptor studies as a nonselective agonist across amylin receptors and the calcitonin receptor",
		receptorTargets: ["AMY1R", "AMY2R", "AMY3R", "CTR"],
		categories: ["weight-management"],
		timing: "flexible",
		halfLife: "159–195 hours in a phase 1b co-administration study",
	},
	{
		slug: "ahk-cu",
		name: "AHK-Cu",
		family: "Copper-Binding Tripeptide",
		mechanism:
			"Copper-complexed alanine-histidine-lysine research peptide; a systemic half-life and clinically validated mechanism have not been established",
		receptorTargets: [],
		categories: ["skin"],
		timing: "flexible",
		halfLife: "Not established",
	},
	{
		slug: "bpc-157",
		name: "BPC-157",
		family: "Gastric Pentadecapeptide",
		mechanism:
			"Nitric oxide pathway upregulation (eNOS), VEGF/VEGFR2-driven angiogenesis, FAK-paxillin cell migration, Egr-1 collagen synthesis, NF-κB anti-inflammatory signaling, growth hormone receptor upregulation",
		receptorTargets: ["eNOS", "VEGFR2", "FAK-paxillin", "Egr-1", "GHR", "NF-κB"],
		categories: ["recovery", "anti-aging", "skin"],
		timing: "flexible",
		halfLife: "~4-6 hours",
	},
	{
		slug: "tb500",
		name: "TB-500",
		family: "Thymosin Beta-4 Fragment",
		mechanism:
			"G-actin sequestration driving actin polymerization, ILK-PI3K-Akt cell survival signaling, VEGF-mediated angiogenesis, MMP-driven ECM remodeling, NF-κB anti-inflammatory modulation",
		receptorTargets: ["G-actin", "ILK-PI3K-Akt", "VEGF", "MMP-2/9", "NF-κB"],
		categories: ["recovery", "anti-aging"],
		timing: "flexible",
		halfLife: "~2-6 hours (biological effect 48-72h)",
	},
	{
		slug: "retatrutide",
		name: "Retatrutide",
		family: "Incretin Triple Agonist",
		mechanism:
			"Simultaneous GLP-1R, GIPR, and GCGR agonism: GLP-1R enhances insulin secretion, suppresses glucagon, delays gastric emptying, reduces appetite; GIPR potentiates insulin and attenuates nausea; GCGR stimulates hepatic lipolysis, thermogenesis, and energy expenditure",
		receptorTargets: ["GLP-1R", "GIPR", "GCGR"],
		categories: ["weight-management"],
		timing: "flexible",
		halfLife: "~6 days",
	},
	{
		slug: "tirzepatid",
		name: "Tirzepatid",
		family: "Incretin Dual Agonist",
		mechanism:
			"GLP-1R and GIPR dual agonism: glucose-dependent insulin secretion, appetite suppression via hypothalamic satiety circuits, improved insulin sensitivity in adipose tissue, brown adipose tissue activation via GIPR",
		receptorTargets: ["GLP-1R", "GIPR"],
		categories: ["weight-management"],
		timing: "flexible",
		halfLife: "~5 days",
	},
	{
		slug: "mt-2",
		name: "Melanotan II",
		family: "Melanocortin Peptide Analogue",
		mechanism:
			"Non-selective melanocortin receptor agonism: MC1R-driven eumelanin synthesis for tanning, MC3R/MC4R hypothalamic appetite suppression, MC4R pro-erectile effects via oxytocin pathways",
		receptorTargets: ["MC1R", "MC3R", "MC4R", "MC5R"],
		categories: ["tanning"],
		timing: "evening",
		halfLife: "~1-2 hours",
	},
	{
		slug: "semax",
		name: "Semax",
		family: "ACTH Fragment Analog",
		mechanism:
			"Melanocortin MC3/MC4 receptor modulation; upregulation of BDNF, NGF, and TrkB expression; enhanced dopaminergic and serotonergic signaling contributing to focus, memory consolidation, and neuroprotection research",
		receptorTargets: ["MC3R", "MC4R", "BDNF", "NGF", "TrkB"],
		categories: ["cognitive"],
		timing: "morning",
		halfLife: "~2-3 minutes (effects persist hours)",
	},
	{
		slug: "selank",
		name: "Selank",
		family: "Anxiolytic Heptapeptide",
		mechanism:
			"Allosteric GABA-A receptor modulation; BDNF and NGF expression modulation; enkephalin stabilization via enkephalinase inhibition; serotonergic pathway involvement supporting calm-focus research",
		receptorTargets: ["GABA-A", "BDNF", "NGF", "enkephalinase"],
		categories: ["cognitive"],
		timing: "morning",
		halfLife: "~2-3 minutes (effects persist hours)",
	},
	{
		slug: "tesamorelin",
		name: "Tesamorelin",
		family: "GHRH Analog",
		mechanism:
			"GHRH receptor agonist; stimulates pulsatile endogenous growth hormone release from anterior pituitary; reduces visceral adipose tissue; improves triglyceride levels and body composition",
		receptorTargets: ["GHRH-R"],
		categories: ["anti-aging", "weight-management"],
		timing: "evening",
		halfLife: "~26-38 min (pulsatile GH effects persist hours)",
	},
	{
		slug: "ghk-cu",
		name: "GHK-Cu",
		family: "Copper Peptide Complex",
		mechanism:
			"Copper(II) ion chelation and delivery; upregulates over 4,000 tissue remodeling genes; promotes collagen I/III/V and elastin synthesis; stimulates decorin and glycosaminoglycan production; activates antioxidant defense genes (SOD, glutathione); anti-inflammatory via IL-6/TNF-α suppression",
		receptorTargets: ["Collagen I/III/V", "Elastin", "Decorin", "SOD", "TGF-β"],
		categories: ["recovery", "anti-aging", "skin"],
		timing: "flexible",
		halfLife: "~30 minutes (plasma)",
	},
	{
		slug: "hgh-191aa",
		name: "HGH 191AA",
		family: "Recombinant Human Growth Hormone",
		mechanism:
			"Full-length 191-amino-acid recombinant somatropin; direct GH receptor (GHR) activation drives JAK2-STAT5 signaling, hepatic IGF-1 release, lipolysis via HSL/ATGL upregulation, mTOR-mediated protein synthesis, and growth-plate chondrogenesis",
		receptorTargets: ["GHR", "JAK2-STAT5", "IGF-1R", "HSL/ATGL"],
		categories: ["anti-aging", "weight-management", "recovery"],
		timing: "evening",
		halfLife: "~20 min plasma (IGF-1 mediated effects 12-24h)",
	},
	{
		slug: "nad-plus",
		name: "NAD+",
		family: "Pyridine Nucleotide Cofactor",
		mechanism:
			"Essential coenzyme for >500 redox reactions; substrate for sirtuins (SIRT1-7) driving longevity gene expression, PARP1/2-mediated DNA repair, CD38-regulated calcium signaling, and mitochondrial Complex I electron transport for ATP synthesis",
		receptorTargets: ["SIRT1-7", "PARP1/2", "CD38", "Complex I"],
		categories: ["anti-aging", "cognitive"],
		timing: "morning",
		halfLife: "~1-2 h plasma",
	},
	{
		slug: "mots-c",
		name: "MOTS-c",
		family: "Mitochondrial-Derived Peptide",
		mechanism:
			"AMPK pathway activation via folate-methionine cycle modulation: inhibits MTHFR, driving AICAR accumulation that activates AMPK; enhances skeletal-muscle glucose uptake, fatty-acid oxidation, and insulin sensitivity; translocates to the nucleus under metabolic stress to co-regulate NRF2 antioxidant and NF-κB anti-inflammatory gene programs; upregulates PGC-1α-driven mitochondrial biogenesis",
		receptorTargets: ["AMPK", "MTHFR", "PGC-1α", "NRF2", "NF-κB"],
		categories: ["anti-aging", "weight-management"],
		timing: "morning",
		halfLife: "~12 hours",
	},
	{
		slug: "cjc-1295",
		name: "CJC-1295 (No DAC)",
		family: "GHRH Analog (Mod GRF 1-29)",
		mechanism:
			"GHRH receptor agonist (short-acting, no DAC): stimulates pituitary somatotrophs via the Gs-cAMP-PKA-CREB axis to drive pulsatile endogenous growth hormone release and downstream IGF-1; without albumin binding, GH pulses remain physiologic",
		receptorTargets: ["GHRH-R", "Gs-cAMP-PKA", "Pit-1"],
		categories: ["anti-aging", "recovery"],
		timing: "evening",
		halfLife: "~30 minutes",
	},
	{
		slug: "kpv",
		name: "KPV",
		family: "alpha-MSH Tripeptide (Lys-Pro-Val)",
		mechanism:
			"Anti-inflammatory tripeptide acting largely intracellularly and receptor-independently: inhibits NF-κB nuclear translocation, stabilizes IκB-α, and suppresses MAPK signaling; enters cells via the PepT1 transporter; researched for gut and skin barrier integrity and wound healing",
		receptorTargets: ["NF-κB", "IκB-α", "MAPK", "PepT1"],
		categories: ["recovery", "skin"],
		timing: "flexible",
		halfLife: "~minutes (rapid peptidase degradation)",
	},
	{
		slug: "thymosin-alpha-1",
		name: "Thymosin Alpha-1",
		family: "Thymic Immunopeptide (28-aa)",
		mechanism:
			"Immune-modulating thymic peptide: enhances T-cell maturation and dendritic-cell activation via TLR2/TLR9-MyD88 signaling, promotes Th1 cytokine responses and NK-cell activity; researched for immune resilience and recovery",
		receptorTargets: ["TLR2", "TLR9", "MyD88"],
		categories: ["recovery", "anti-aging"],
		timing: "flexible",
		halfLife: "~2 hours",
	},
];

export const STACK_COMBINATIONS: StackCombination[] = [
	{
		peptides: ["bpc-157", "mt-2"],
		synergyScore: 2,
		rationale:
			"No known synergy — different biological targets. Indirect dopaminergic pathway convergence warrants caution.",
		rationaleKey: "bpc157_mt2",
		timingAdvice: "BPC-157 morning, MT-2 evening for temporal separation.",
		timingAdviceKey: "bpc157_mt2",
		overlapWarning:
			"Indirect dopaminergic convergence — MT-2 MC4R and BPC-157 D1/D2 modulation both influence mesolimbic dopamine.",
		overlapWarningKey: "bpc157_mt2",
	},
	{
		peptides: ["bpc-157", "retatrutide"],
		synergyScore: 4,
		rationale:
			"BPC-157's gastroprotective and NO-pathway effects complement Retatrutide's metabolic remodeling by protecting GI mucosa from incretin-related side effects.",
		rationaleKey: "bpc157_retatrutide",
		timingAdvice: "Retatrutide once weekly; BPC-157 daily. No timing coordination needed.",
		timingAdviceKey: "bpc157_retatrutide",
	},
	{
		peptides: ["bpc-157", "tb500"],
		synergyScore: 5,
		rationale:
			"The classic recovery combination — BPC-157 drives angiogenesis and collagen remodeling via NO/VEGF, while TB-500 promotes cell migration via actin polymerization. Complementary, non-overlapping mechanisms.",
		rationaleKey: "bpc157_tb500",
		timingAdvice:
			"Both can be administered same day. Morning and evening or simultaneously at different injection sites.",
		timingAdviceKey: "bpc157_tb500",
	},
	{
		peptides: ["bpc-157", "tirzepatid"],
		synergyScore: 4,
		rationale:
			"BPC-157's cytoprotective NO-pathway effects complement Tirzepatid by protecting GI mucosa and supporting pancreatic beta-cell integrity during metabolic adaptation.",
		rationaleKey: "bpc157_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; BPC-157 daily. No timing coordination needed.",
		timingAdviceKey: "bpc157_tirzepatid",
	},
	{
		peptides: ["mt-2", "tesamorelin"],
		synergyScore: 2,
		rationale:
			"Independent mechanisms — melanocortin tanning versus GHRH-mediated GH release. No known synergy or antagonism.",
		rationaleKey: "mt2_tesamorelin",
		timingAdvice: "Both evening-compatible. No interaction.",
		timingAdviceKey: "mt2_tesamorelin",
	},
	{
		peptides: ["mt-2", "retatrutide"],
		synergyScore: 2,
		rationale:
			"No direct receptor overlap, but additive appetite suppression via parallel central pathways (MC4R + GLP-1R) may cause excessive caloric restriction.",
		rationaleKey: "mt2_retatrutide",
		timingAdvice: "Retatrutide morning, MT-2 evening to separate peak effects.",
		timingAdviceKey: "mt2_retatrutide",
		overlapWarning:
			"Additive appetite suppression — both reduce food intake via independent central mechanisms. Monitor caloric intake.",
		overlapWarningKey: "mt2_retatrutide",
	},
	{
		peptides: ["mt-2", "tb500"],
		synergyScore: 2,
		rationale: "Minimal synergy — entirely orthogonal receptor systems with no shared research endpoints.",
		rationaleKey: "mt2_tb500",
		timingAdvice: "TB-500 morning or post-workout; MT-2 evening. No interaction.",
		timingAdviceKey: "mt2_tb500",
	},
	{
		peptides: ["mt-2", "tirzepatid"],
		synergyScore: 2,
		rationale:
			"Functional overlap in appetite suppression (MC4R + GLP-1R pathways) without direct receptor redundancy. Compounded nausea and cardiovascular risk.",
		rationaleKey: "mt2_tirzepatid",
		timingAdvice: "Tirzepatid morning, MT-2 evening. Begin MT-2 after Tirzepatid dose is stabilized.",
		timingAdviceKey: "mt2_tirzepatid",
		overlapWarning: "Compounded appetite suppression and nausea risk from parallel central pathways.",
		overlapWarningKey: "mt2_tirzepatid",
	},
	{
		peptides: ["retatrutide", "tesamorelin"],
		synergyScore: 3,
		rationale:
			"Independent metabolic pathways — GHRH-R mediated GH release versus GLP-1R/GIPR/GCGR incretin agonism. Complementary body composition effects via different mechanisms.",
		rationaleKey: "retatrutide_tesamorelin",
		timingAdvice: "Retatrutide once weekly; Tesamorelin daily evening. No coordination needed.",
		timingAdviceKey: "retatrutide_tesamorelin",
	},
	{
		peptides: ["retatrutide", "tb500"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms — TB-500 for tissue repair, Retatrutide for metabolic regulation. TB-500 may support connective tissue adaptation during rapid body composition changes.",
		rationaleKey: "retatrutide_tb500",
		timingAdvice: "Retatrutide once weekly; TB-500 2-3x weekly on separate days.",
		timingAdviceKey: "retatrutide_tb500",
	},
	{
		peptides: ["retatrutide", "tirzepatid"],
		synergyScore: 1,
		rationale:
			"Both are full agonists at GLP-1R and GIPR — co-administration creates redundant receptor saturation with compounded GI toxicity, hypoglycemia risk, and cardiovascular stress. Not recommended.",
		rationaleKey: "retatrutide_tirzepatid",
		timingAdvice: "Choose one, not both. If transitioning, allow 15-20 day washout.",
		timingAdviceKey: "retatrutide_tirzepatid",
		overlapWarning:
			"Critical receptor overlap at GLP-1R and GIPR. Retatrutide already encompasses all of Tirzepatid's pharmacology plus GCGR agonism.",
		overlapWarningKey: "retatrutide_tirzepatid",
	},
	{
		peptides: ["tb500", "tirzepatid"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms — TB-500 for connective tissue recovery, Tirzepatid for metabolic support. Useful in body recomposition contexts.",
		rationaleKey: "tb500_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; TB-500 2-3x weekly. No coordination needed.",
		timingAdviceKey: "tb500_tirzepatid",
	},
	{
		peptides: ["bpc-157", "tesamorelin"],
		synergyScore: 3,
		rationale:
			"GH-axis complement — Tesamorelin's pulsatile GH stimulation via GHRH-R enhances the anabolic environment for BPC-157's angiogenesis and collagen remodeling. No receptor overlap.",
		rationaleKey: "bpc157_tesamorelin",
		timingAdvice: "BPC-157 daily (any time); Tesamorelin evening before bed. No coordination needed.",
		timingAdviceKey: "bpc157_tesamorelin",
	},
	{
		peptides: ["bpc-157", "selank"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms with potential gut-brain axis synergy — BPC-157 supports intestinal integrity and dopaminergic signaling while Selank provides allosteric GABA-A modulation and neurotrophic support. No receptor overlap.",
		rationaleKey: "bpc157_selank",
		timingAdvice: "BPC-157 daily (any time); Selank intranasal morning. No coordination needed.",
		timingAdviceKey: "bpc157_selank",
	},
	{
		peptides: ["bpc-157", "semax"],
		synergyScore: 3,
		rationale:
			"Complementary tissue-plus-CNS profile — BPC-157 supports peripheral tissue integrity, angiogenesis via VEGFR2, and dopaminergic signaling research, while Semax provides centrally-directed melanocortin-driven BDNF and TrkB upregulation. No receptor overlap.",
		rationaleKey: "bpc157_semax",
		timingAdvice: "BPC-157 daily (any time); Semax intranasal morning. No coordination needed.",
		timingAdviceKey: "bpc157_semax",
	},
	{
		peptides: ["mt-2", "selank"],
		synergyScore: 2,
		rationale:
			"Orthogonal receptor systems — melanocortin (MC1R/MC3R/MC4R) versus GABA-A allosteric modulation. No known synergy or antagonism.",
		rationaleKey: "mt2_selank",
		timingAdvice: "MT-2 evening; Selank intranasal morning. No interaction.",
		timingAdviceKey: "mt2_selank",
	},
	{
		peptides: ["mt-2", "semax"],
		synergyScore: 2,
		rationale:
			"Both peptides act on melanocortin receptors — MT-2 is a non-selective MC1R/MC3R/MC4R agonist while Semax activates MC3R/MC4R. Potential competitive binding at shared receptors with unpredictable net effects.",
		rationaleKey: "mt2_semax",
		timingAdvice:
			"Separate by at least several hours — MT-2 evening, Semax morning. Monitor for unexpected central effects.",
		timingAdviceKey: "mt2_semax",
		overlapWarning:
			"Both peptides bind melanocortin receptors (MC3R/MC4R). Potential competitive binding may reduce efficacy of both or produce unpredictable central effects.",
		overlapWarningKey: "mt2_semax",
	},
	{
		peptides: ["retatrutide", "selank"],
		synergyScore: 2,
		rationale:
			"Entirely independent pharmacology — incretin triple-agonism versus GABAergic modulation. No known interaction.",
		rationaleKey: "retatrutide_selank",
		timingAdvice: "Retatrutide once weekly; Selank intranasal morning. No coordination needed.",
		timingAdviceKey: "retatrutide_selank",
	},
	{
		peptides: ["retatrutide", "semax"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology — metabolic incretin signaling versus melanocortin-driven neurotrophic effects. No known interaction.",
		rationaleKey: "retatrutide_semax",
		timingAdvice: "Retatrutide once weekly; Semax intranasal morning. No coordination needed.",
		timingAdviceKey: "retatrutide_semax",
	},
	{
		peptides: ["selank", "tesamorelin"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology — GABAergic anxiolytic modulation versus GHRH-mediated endocrine signaling. No known interaction.",
		rationaleKey: "selank_tesamorelin",
		timingAdvice: "Selank intranasal morning; Tesamorelin evening. No interaction.",
		timingAdviceKey: "selank_tesamorelin",
	},
	{
		peptides: ["selank", "semax"],
		synergyScore: 5,
		rationale:
			"The classic nootropic combination — Semax drives focus and BDNF upregulation via melanocortin and TrkB pathways, while Selank provides allosteric GABA-A modulation for calm and anxiety reduction. Complementary, non-overlapping mechanisms widely researched together.",
		rationaleKey: "selank_semax",
		timingAdvice:
			"Both intranasal, morning. Administer Semax first, then Selank 10-15 minutes later to avoid nasal absorption competition.",
		timingAdviceKey: "selank_semax",
	},
	{
		peptides: ["selank", "tb500"],
		synergyScore: 2,
		rationale:
			"Orthogonal systems — GABAergic neuromodulation versus actin polymerization and systemic tissue repair. No known synergy.",
		rationaleKey: "selank_tb500",
		timingAdvice: "TB-500 2-3× weekly; Selank intranasal morning. No interaction.",
		timingAdviceKey: "selank_tb500",
	},
	{
		peptides: ["selank", "tirzepatid"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology — GABAergic modulation versus incretin dual-agonism. No known interaction.",
		rationaleKey: "selank_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; Selank intranasal morning. No coordination needed.",
		timingAdviceKey: "selank_tirzepatid",
	},
	{
		peptides: ["semax", "tesamorelin"],
		synergyScore: 2,
		rationale:
			"Independent CNS versus endocrine pathways — melanocortin-driven neurotrophic effects versus GHRH-mediated GH release. No known interaction.",
		rationaleKey: "semax_tesamorelin",
		timingAdvice: "Semax intranasal morning; Tesamorelin evening. No interaction.",
		timingAdviceKey: "semax_tesamorelin",
	},
	{
		peptides: ["semax", "tb500"],
		synergyScore: 3,
		rationale:
			"Complementary profiles targeting different systems — TB-500 drives peripheral tissue repair, actin remodeling, and angiogenesis, while Semax provides centrally-directed melanocortin/TrkB-mediated neurotrophic effects. Useful when a research protocol needs both recovery and focus endpoints.",
		rationaleKey: "semax_tb500",
		timingAdvice: "TB-500 2-3× weekly; Semax intranasal morning. No interaction.",
		timingAdviceKey: "semax_tb500",
	},
	{
		peptides: ["tb500", "tesamorelin"],
		synergyScore: 3,
		rationale:
			"GH-axis complement — Tesamorelin's pulsatile GH stimulation supports TB-500's actin polymerization and tissue remodeling by elevating the anabolic environment. No receptor overlap.",
		rationaleKey: "tb500_tesamorelin",
		timingAdvice: "TB-500 2-3x weekly; Tesamorelin daily evening. No coordination needed.",
		timingAdviceKey: "tb500_tesamorelin",
	},
	{
		peptides: ["tesamorelin", "tirzepatid"],
		synergyScore: 3,
		rationale:
			"Complementary body composition mechanisms — GHRH-mediated lipolysis and GH-driven lean mass preservation plus GLP-1R/GIPR-mediated appetite suppression and insulin sensitization. No receptor overlap.",
		rationaleKey: "tesamorelin_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; Tesamorelin daily evening. No coordination needed.",
		timingAdviceKey: "tesamorelin_tirzepatid",
	},
	{
		peptides: ["semax", "tirzepatid"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology — incretin signaling versus melanocortin-driven neurotrophic effects. No known interaction.",
		rationaleKey: "semax_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; Semax intranasal morning. No coordination needed.",
		timingAdviceKey: "semax_tirzepatid",
	},
	// GHK-Cu combinations
	{
		peptides: ["bpc-157", "ghk-cu"],
		synergyScore: 5,
		rationale:
			"Excellent tissue repair combination — BPC-157 drives angiogenesis via NO/VEGF pathways while GHK-Cu promotes collagen/elastin synthesis and ECM remodeling via copper-mediated gene activation. Complementary, non-overlapping mechanisms.",
		rationaleKey: "bpc157_ghkcu",
		timingAdvice: "Both can be administered same day. BPC-157 subcutaneous; GHK-Cu subcutaneous or topical.",
		timingAdviceKey: "bpc157_ghkcu",
	},
	{
		peptides: ["ghk-cu", "mt-2"],
		synergyScore: 2,
		rationale:
			"Orthogonal receptor systems — copper peptide-driven ECM remodeling versus melanocortin receptor-mediated melanogenesis. No known synergy or antagonism.",
		rationaleKey: "ghkcu_mt2",
		timingAdvice: "GHK-Cu any time; MT-2 evening. No interaction.",
		timingAdviceKey: "ghkcu_mt2",
	},
	{
		peptides: ["ghk-cu", "retatrutide"],
		synergyScore: 2,
		rationale:
			"Independent mechanisms — copper peptide tissue remodeling versus incretin triple-agonist metabolic regulation. No known interaction.",
		rationaleKey: "ghkcu_retatrutide",
		timingAdvice: "Retatrutide once weekly; GHK-Cu daily. No coordination needed.",
		timingAdviceKey: "ghkcu_retatrutide",
	},
	{
		peptides: ["ghk-cu", "selank"],
		synergyScore: 2,
		rationale:
			"Orthogonal systems — copper peptide tissue repair versus GABAergic neuromodulation. No known synergy.",
		rationaleKey: "ghkcu_selank",
		timingAdvice: "GHK-Cu any time; Selank intranasal morning. No interaction.",
		timingAdviceKey: "ghkcu_selank",
	},
	{
		peptides: ["ghk-cu", "semax"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology — copper-mediated tissue remodeling versus melanocortin-driven neurotrophic effects. No known interaction.",
		rationaleKey: "ghkcu_semax",
		timingAdvice: "GHK-Cu any time; Semax intranasal morning. No interaction.",
		timingAdviceKey: "ghkcu_semax",
	},
	{
		peptides: ["ghk-cu", "tb500"],
		synergyScore: 5,
		rationale:
			"The regeneration powerhouse — TB-500 drives cell migration via actin polymerization and angiogenesis, while GHK-Cu activates collagen/elastin synthesis and ECM remodeling. Complementary, non-overlapping tissue repair mechanisms.",
		rationaleKey: "ghkcu_tb500",
		timingAdvice:
			"Both can be administered same day. TB-500 2-3x weekly; GHK-Cu daily. No coordination needed.",
		timingAdviceKey: "ghkcu_tb500",
	},
	{
		peptides: ["ghk-cu", "tesamorelin"],
		synergyScore: 3,
		rationale:
			"Complementary via GH axis — Tesamorelin's pulsatile GH stimulation enhances the anabolic environment for GHK-Cu's collagen and tissue remodeling effects. No receptor overlap.",
		rationaleKey: "ghkcu_tesamorelin",
		timingAdvice: "GHK-Cu daily (any time); Tesamorelin evening. No coordination needed.",
		timingAdviceKey: "ghkcu_tesamorelin",
	},
	{
		peptides: ["ghk-cu", "tirzepatid"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology — copper peptide tissue remodeling versus incretin dual-agonist metabolic regulation. No known interaction.",
		rationaleKey: "ghkcu_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; GHK-Cu daily. No coordination needed.",
		timingAdviceKey: "ghkcu_tirzepatid",
	},
	// HGH 191AA combinations
	{
		peptides: ["bpc-157", "hgh-191aa"],
		synergyScore: 4,
		rationale:
			"Anabolic-plus-tissue-repair pairing — HGH 191AA's IGF-1-mediated protein synthesis and lean-mass support combine with BPC-157's NO/VEGF-driven angiogenesis and collagen remodeling. Independent receptor systems, complementary recovery profile.",
		rationaleKey: "bpc157_hgh191aa",
		timingAdvice: "BPC-157 daily (any time); HGH 191AA evening before bed for natural pulsatile alignment.",
		timingAdviceKey: "bpc157_hgh191aa",
	},
	{
		peptides: ["ghk-cu", "hgh-191aa"],
		synergyScore: 4,
		rationale:
			"Strong skin and recovery combination — HGH 191AA drives systemic anabolism via GHR/IGF-1, while GHK-Cu activates collagen/elastin synthesis through copper-mediated ECM remodeling. Non-overlapping mechanisms with shared connective-tissue endpoints.",
		rationaleKey: "ghkcu_hgh191aa",
		timingAdvice: "GHK-Cu any time (subcutaneous or topical); HGH 191AA evening before bed.",
		timingAdviceKey: "ghkcu_hgh191aa",
	},
	{
		peptides: ["hgh-191aa", "mt-2"],
		synergyScore: 2,
		rationale:
			"Orthogonal receptor systems — somatropin acts on the GHR/IGF-1 axis, MT-2 on melanocortin receptors. No known synergy or antagonism.",
		rationaleKey: "hgh191aa_mt2",
		timingAdvice: "HGH 191AA evening before bed; MT-2 evening with food. Space dosing 30+ minutes apart.",
		timingAdviceKey: "hgh191aa_mt2",
	},
	{
		peptides: ["hgh-191aa", "nad-plus"],
		synergyScore: 4,
		rationale:
			"Anabolic-plus-mitochondrial pairing — HGH 191AA drives IGF-1-mediated protein synthesis and lean-mass support, while NAD+ fuels sirtuin-driven longevity gene expression and mitochondrial Complex I capacity. Energetic substrate supports anabolic signaling.",
		rationaleKey: "hgh191aa_nadplus",
		timingAdvice: "NAD+ morning for circadian alignment with energy demands; HGH 191AA evening before bed.",
		timingAdviceKey: "hgh191aa_nadplus",
	},
	{
		peptides: ["hgh-191aa", "retatrutide"],
		synergyScore: 3,
		rationale:
			"Independent metabolic mechanisms — Retatrutide's GLP-1R/GIPR/GCGR triple-agonism reduces appetite and improves insulin sensitivity, while HGH 191AA drives lean-mass via IGF-1. GH-induced insulin antagonism is partially offset by Retatrutide's incretin effects; glucose monitoring recommended.",
		rationaleKey: "hgh191aa_retatrutide",
		timingAdvice: "Retatrutide once weekly; HGH 191AA daily evening. No timing coordination required.",
		timingAdviceKey: "hgh191aa_retatrutide",
		overlapWarning:
			"GH antagonizes insulin sensitivity at high doses; Retatrutide improves it. Net glucose handling depends on individual response — monitor fasting glucose.",
		overlapWarningKey: "hgh191aa_retatrutide",
	},
	{
		peptides: ["hgh-191aa", "selank"],
		synergyScore: 2,
		rationale:
			"Orthogonal pharmacology — endocrine GH/IGF-1 signaling versus GABA-A allosteric anxiolytic modulation. No known interaction.",
		rationaleKey: "hgh191aa_selank",
		timingAdvice: "Selank intranasal morning; HGH 191AA evening before bed. No interaction.",
		timingAdviceKey: "hgh191aa_selank",
	},
	{
		peptides: ["hgh-191aa", "semax"],
		synergyScore: 2,
		rationale:
			"Independent endocrine versus CNS pathways — somatropin's GHR/IGF-1 axis versus Semax's melanocortin/BDNF-driven neurotrophic effects. No known interaction.",
		rationaleKey: "hgh191aa_semax",
		timingAdvice: "Semax intranasal morning; HGH 191AA evening before bed. No interaction.",
		timingAdviceKey: "hgh191aa_semax",
	},
	{
		peptides: ["hgh-191aa", "tb500"],
		synergyScore: 4,
		rationale:
			"Classic anabolic-plus-recovery pairing — HGH 191AA's IGF-1-mediated protein synthesis and lean-mass support combine with TB-500's actin-polymerization-driven cell migration and angiogenesis. Independent receptor systems, complementary tissue-repair profile.",
		rationaleKey: "hgh191aa_tb500",
		timingAdvice: "TB-500 2-3x weekly; HGH 191AA daily evening. No timing coordination required.",
		timingAdviceKey: "hgh191aa_tb500",
	},
	{
		peptides: ["hgh-191aa", "tesamorelin"],
		synergyScore: 1,
		rationale:
			"Mechanistic redundancy — Tesamorelin stimulates endogenous GH release via GHRH-R; HGH 191AA IS exogenous GH. Co-administration produces supraphysiological GH levels with no additional benefit and an amplified side-effect profile (insulin resistance, edema, carpal-tunnel risk). Not recommended.",
		rationaleKey: "hgh191aa_tesamorelin",
		timingAdvice: "Choose one. If transitioning, allow a 1-2 week washout before starting the other.",
		timingAdviceKey: "hgh191aa_tesamorelin",
		overlapWarning:
			"Critical mechanistic redundancy — both elevate circulating GH/IGF-1. Stacking provides no synergy and compounds GH-related side effects.",
		overlapWarningKey: "hgh191aa_tesamorelin",
	},
	{
		peptides: ["hgh-191aa", "tirzepatid"],
		synergyScore: 3,
		rationale:
			"Independent metabolic mechanisms — Tirzepatid's GLP-1R/GIPR dual-agonism improves insulin sensitivity and suppresses appetite, while HGH 191AA drives lean-mass via IGF-1. GH-induced insulin antagonism is partially offset by Tirzepatid; monitor fasting glucose.",
		rationaleKey: "hgh191aa_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; HGH 191AA daily evening. No timing coordination required.",
		timingAdviceKey: "hgh191aa_tirzepatid",
		overlapWarning:
			"GH and incretins exert opposing effects on insulin sensitivity. Net glucose handling depends on individual response — monitor fasting glucose.",
		overlapWarningKey: "hgh191aa_tirzepatid",
	},
	// NAD+ combinations
	{
		peptides: ["bpc-157", "nad-plus"],
		synergyScore: 3,
		rationale:
			"Independent gut-protective and cellular-energy mechanisms — BPC-157 supports GI mucosal integrity and angiogenesis via NO/VEGF, while NAD+ fuels sirtuin-driven cellular maintenance and mitochondrial energy production. Supportive but no direct receptor synergy.",
		rationaleKey: "bpc157_nadplus",
		timingAdvice: "BPC-157 daily (any time); NAD+ morning for circadian alignment with energy demands.",
		timingAdviceKey: "bpc157_nadplus",
	},
	{
		peptides: ["ghk-cu", "nad-plus"],
		synergyScore: 4,
		rationale:
			"Strong anti-aging combination — GHK-Cu drives ECM remodeling and gene-level antioxidant defense (SOD, glutathione), while NAD+ activates SIRT1/SIRT3 longevity pathways and supports mitochondrial biogenesis. Complementary mechanisms targeting tissue and cellular aging in parallel.",
		rationaleKey: "ghkcu_nadplus",
		timingAdvice: "GHK-Cu any time (subcutaneous or topical); NAD+ morning. No coordination needed.",
		timingAdviceKey: "ghkcu_nadplus",
	},
	{
		peptides: ["mt-2", "nad-plus"],
		synergyScore: 2,
		rationale:
			"Orthogonal receptor systems — melanocortin (MC1R/MC3R/MC4R) versus pyridine-nucleotide-mediated metabolic cofactor activity. No known interaction.",
		rationaleKey: "mt2_nadplus",
		timingAdvice: "NAD+ morning; MT-2 evening. No interaction.",
		timingAdviceKey: "mt2_nadplus",
	},
	{
		peptides: ["nad-plus", "retatrutide"],
		synergyScore: 4,
		rationale:
			"Strong metabolic-flexibility pairing — Retatrutide's GLP-1R/GIPR/GCGR triple-agonism drives appetite suppression, insulin sensitization, and GCGR-mediated thermogenesis, while NAD+ fuels SIRT1/SIRT3 mitochondrial biogenesis and fat oxidation. Energetic substrate meets metabolic remodeling.",
		rationaleKey: "nadplus_retatrutide",
		timingAdvice: "Retatrutide once weekly; NAD+ daily morning. No coordination needed.",
		timingAdviceKey: "nadplus_retatrutide",
	},
	{
		peptides: ["nad-plus", "selank"],
		synergyScore: 3,
		rationale:
			"Complementary cognitive support — Selank provides allosteric GABA-A modulation and BDNF/NGF upregulation for calm-focus, while NAD+ supports neuronal mitochondrial energy and SIRT-driven neuroprotection. No receptor overlap.",
		rationaleKey: "nadplus_selank",
		timingAdvice: "Both morning — Selank intranasal, NAD+ subcutaneous or IV. Compatible timing.",
		timingAdviceKey: "nadplus_selank",
	},
	{
		peptides: ["nad-plus", "semax"],
		synergyScore: 4,
		rationale:
			"Strong nootropic combination — Semax drives BDNF/NGF/TrkB upregulation and dopaminergic/serotonergic modulation, while NAD+ fuels neuronal mitochondrial Complex I and SIRT3-driven energy production. Cognitive signaling meets cellular energy substrate.",
		rationaleKey: "nadplus_semax",
		timingAdvice: "Both morning — Semax intranasal first, NAD+ subcutaneous or IV. Compatible timing.",
		timingAdviceKey: "nadplus_semax",
	},
	{
		peptides: ["nad-plus", "tb500"],
		synergyScore: 3,
		rationale:
			"Independent regenerative mechanisms — TB-500 drives actin polymerization for cell migration and angiogenesis, while NAD+ supports cellular energy and sirtuin-mediated tissue maintenance. Supportive but no direct receptor synergy.",
		rationaleKey: "nadplus_tb500",
		timingAdvice: "TB-500 2-3x weekly; NAD+ daily morning. No coordination needed.",
		timingAdviceKey: "nadplus_tb500",
	},
	{
		peptides: ["nad-plus", "tesamorelin"],
		synergyScore: 4,
		rationale:
			"Strong systemic anti-aging pairing — Tesamorelin's pulsatile GH/IGF-1 stimulation drives anabolic remodeling and visceral fat reduction, while NAD+ supports SIRT1/SIRT3 mitochondrial biogenesis and PARP-mediated DNA repair. Endocrine and energetic axes align.",
		rationaleKey: "nadplus_tesamorelin",
		timingAdvice: "NAD+ morning; Tesamorelin evening before bed. No interaction.",
		timingAdviceKey: "nadplus_tesamorelin",
	},
	{
		peptides: ["nad-plus", "tirzepatid"],
		synergyScore: 4,
		rationale:
			"Strong metabolic-flexibility pairing — Tirzepatid's GLP-1R/GIPR dual-agonism drives appetite suppression, insulin sensitization, and brown-adipose activation via GIPR, while NAD+ fuels SIRT1/SIRT3 mitochondrial biogenesis and fat oxidation. Energetic substrate meets metabolic remodeling.",
		rationaleKey: "nadplus_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; NAD+ daily morning. No coordination needed.",
		timingAdviceKey: "nadplus_tirzepatid",
	},
	// MOTS-c combinations
	{
		peptides: ["bpc-157", "mots-c"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms with supportive overlap - BPC-157 drives angiogenesis and tissue repair via the NO/VEGF pathway, while MOTS-c improves the systemic metabolic environment through AMPK activation. No receptor overlap; a healthier metabolic baseline can support recovery research endpoints.",
		rationaleKey: "bpc157_motsc",
		timingAdvice: "BPC-157 daily (any time); MOTS-c once weekly subcutaneous. No coordination needed.",
		timingAdviceKey: "bpc157_motsc",
	},
	{
		peptides: ["ghk-cu", "mots-c"],
		synergyScore: 3,
		rationale:
			"Complementary anti-aging mechanisms - GHK-Cu remodels the extracellular matrix and activates antioxidant-defense genes, while MOTS-c drives AMPK-mediated metabolic flexibility and NRF2 antioxidant signaling. Parallel tissue-level and cellular-energy effects with no receptor overlap.",
		rationaleKey: "ghkcu_motsc",
		timingAdvice: "GHK-Cu daily (any time); MOTS-c once weekly. No coordination needed.",
		timingAdviceKey: "ghkcu_motsc",
	},
	{
		peptides: ["hgh-191aa", "mots-c"],
		synergyScore: 4,
		rationale:
			"Useful counterbalancing pairing - HGH 191AA drives IGF-1-mediated anabolism but can reduce insulin sensitivity at higher doses, while MOTS-c enhances insulin sensitivity and glucose handling via AMPK. MOTS-c may help offset the metabolic downside of growth hormone.",
		rationaleKey: "hgh191aa_motsc",
		timingAdvice:
			"HGH 191AA daily evening; MOTS-c once weekly. No coordination needed; monitor fasting glucose.",
		timingAdviceKey: "hgh191aa_motsc",
	},
	{
		peptides: ["mots-c", "mt-2"],
		synergyScore: 2,
		rationale:
			"Minimal synergy - orthogonal receptor systems. MOTS-c acts on the AMPK metabolic axis; MT-2 acts on melanocortin receptors for tanning. No known interaction.",
		rationaleKey: "motsc_mt2",
		timingAdvice: "MOTS-c once weekly; MT-2 evening. No interaction.",
		timingAdviceKey: "motsc_mt2",
	},
	{
		peptides: ["mots-c", "nad-plus"],
		synergyScore: 5,
		rationale:
			"The mitochondrial powerhouse pairing - MOTS-c activates AMPK and upregulates PGC-1α-driven mitochondrial biogenesis, while NAD+ fuels the sirtuins (SIRT1/SIRT3) and Complex I that those mitochondria depend on. AMPK and sirtuin signaling are tightly interlinked energy-sensing pathways that converge on PGC-1α.",
		rationaleKey: "motsc_nadplus",
		timingAdvice:
			"Both morning for circadian alignment with energy metabolism. MOTS-c once weekly, NAD+ daily.",
		timingAdviceKey: "motsc_nadplus",
	},
	{
		peptides: ["mots-c", "retatrutide"],
		synergyScore: 4,
		rationale:
			"Strong metabolic pairing - Retatrutide's GLP-1R/GIPR/GCGR triple-agonism drives appetite suppression and energy expenditure, while MOTS-c improves insulin sensitivity and mitochondrial fat oxidation via AMPK. Complementary routes to metabolic remodeling.",
		rationaleKey: "motsc_retatrutide",
		timingAdvice: "Retatrutide once weekly; MOTS-c once weekly, ideally on a separate day.",
		timingAdviceKey: "motsc_retatrutide",
		overlapWarning:
			"Both can lower blood glucose through independent mechanisms. Monitor fasting glucose, especially alongside other glucose-lowering compounds.",
		overlapWarningKey: "motsc_retatrutide",
	},
	{
		peptides: ["mots-c", "selank"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology - Selank provides GABAergic anxiolytic modulation, while MOTS-c drives AMPK metabolic signaling. No known interaction.",
		rationaleKey: "motsc_selank",
		timingAdvice: "Selank intranasal morning; MOTS-c once weekly. No interaction.",
		timingAdviceKey: "motsc_selank",
	},
	{
		peptides: ["mots-c", "semax"],
		synergyScore: 2,
		rationale:
			"Largely independent pathways - Semax drives melanocortin and BDNF/TrkB neurotrophic signaling, while MOTS-c supports cellular energy metabolism via AMPK. Minor indirect support of neuronal mitochondrial energy, but no direct synergy.",
		rationaleKey: "motsc_semax",
		timingAdvice: "Semax intranasal morning; MOTS-c once weekly. No interaction.",
		timingAdviceKey: "motsc_semax",
	},
	{
		peptides: ["mots-c", "tb500"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms with supportive overlap - TB-500 drives actin-mediated cell migration and tissue repair, while MOTS-c improves the systemic metabolic environment via AMPK. No receptor overlap.",
		rationaleKey: "motsc_tb500",
		timingAdvice: "TB-500 2-3x weekly; MOTS-c once weekly. No coordination needed.",
		timingAdviceKey: "motsc_tb500",
	},
	{
		peptides: ["mots-c", "tesamorelin"],
		synergyScore: 4,
		rationale:
			"Complementary body-composition mechanisms - Tesamorelin's GHRH-mediated GH release reduces visceral fat and supports lean mass, while MOTS-c enhances AMPK-driven fat oxidation and insulin sensitivity, helping offset GH-related insulin antagonism.",
		rationaleKey: "motsc_tesamorelin",
		timingAdvice:
			"Tesamorelin daily evening; MOTS-c once weekly. No coordination needed; monitor fasting glucose.",
		timingAdviceKey: "motsc_tesamorelin",
	},
	{
		peptides: ["mots-c", "tirzepatid"],
		synergyScore: 4,
		rationale:
			"Strong metabolic pairing - Tirzepatid's GLP-1R/GIPR dual-agonism suppresses appetite and improves insulin sensitivity, while MOTS-c adds AMPK-mediated mitochondrial fat oxidation and metabolic flexibility. Complementary, non-overlapping metabolic mechanisms.",
		rationaleKey: "motsc_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; MOTS-c once weekly, ideally on a separate day.",
		timingAdviceKey: "motsc_tirzepatid",
		overlapWarning:
			"Both improve glucose handling through independent mechanisms. Monitor fasting glucose, especially alongside other glucose-lowering compounds.",
		overlapWarningKey: "motsc_tirzepatid",
	},
	{
		peptides: ["bpc-157", "cjc-1295"],
		synergyScore: 3,
		rationale:
			"GH-axis complement with no receptor overlap - CJC-1295's GHRH-R agonism drives pulsatile endogenous GH/IGF-1 release that raises the anabolic baseline for BPC-157's NO/VEGFR2-driven angiogenesis and Egr-1 collagen synthesis. Independent pathways, supportive recovery profile.",
		rationaleKey: "bpc157_cjc1295",
		timingAdvice:
			"BPC-157 daily (any time); CJC-1295 evening before bed to align with the natural nocturnal GH pulse. No coordination needed.",
		timingAdviceKey: "bpc157_cjc1295",
	},
	{
		peptides: ["bpc-157", "kpv"],
		synergyScore: 5,
		rationale:
			"Excellent gut-and-tissue repair pairing - BPC-157 drives angiogenesis and mucosal healing via the NO/VEGFR2 pathway, while KPV delivers receptor-independent anti-inflammatory action by inhibiting NF-κB nuclear translocation and stabilizing IκB-α. The two converge on NF-κB suppression from different angles (BPC-157 upstream, KPV intracellular) and are widely co-researched for gut-barrier and wound-healing endpoints.",
		rationaleKey: "bpc157_kpv",
		timingAdvice:
			"Both flexible and can be administered same day. BPC-157 subcutaneous; KPV oral or subcutaneous. No timing coordination needed.",
		timingAdviceKey: "bpc157_kpv",
	},
	{
		peptides: ["bpc-157", "thymosin-alpha-1"],
		synergyScore: 4,
		rationale:
			"Complementary immune-plus-tissue-repair pairing — Thymosin Alpha-1 drives T-cell maturation and Th1 immunity via TLR2/TLR9 signaling, while BPC-157 promotes angiogenesis and collagen remodeling through the NO/eNOS and VEGF/VEGFR2 pathways. Non-overlapping mechanisms; a strengthened immune baseline supports recovery research endpoints.",
		rationaleKey: "bpc157_thymosinalpha1",
		timingAdvice:
			"Both flexible — administer same day. BPC-157 daily subcutaneous; Thymosin Alpha-1 typically 2-3x weekly. No coordination needed.",
		timingAdviceKey: "bpc157_thymosinalpha1",
	},
	{
		peptides: ["cjc-1295", "ghk-cu"],
		synergyScore: 3,
		rationale:
			"Complementary via GH axis - CJC-1295's pulsatile GH/IGF-1 stimulation enhances the anabolic environment for GHK-Cu's copper-mediated collagen/elastin synthesis and ECM remodeling. No receptor overlap; shared connective-tissue endpoints.",
		rationaleKey: "cjc1295_ghkcu",
		timingAdvice:
			"GHK-Cu daily (subcutaneous or topical); CJC-1295 evening before bed. No coordination needed.",
		timingAdviceKey: "cjc1295_ghkcu",
	},
	{
		peptides: ["cjc-1295", "hgh-191aa"],
		synergyScore: 1,
		rationale:
			"Mechanistic redundancy - CJC-1295 stimulates endogenous GH release via GHRH-R, while HGH 191AA IS exogenous GH acting directly on GHR/JAK2-STAT5. Co-administration produces supraphysiological GH levels with no additional benefit and an amplified side-effect profile (insulin resistance, edema, carpal-tunnel risk). Not recommended.",
		rationaleKey: "cjc1295_hgh191aa",
		timingAdvice:
			"Choose one, not both. If transitioning, allow a 1-2 week washout before starting the other.",
		timingAdviceKey: "cjc1295_hgh191aa",
		overlapWarning:
			"Critical mechanistic redundancy - both elevate circulating GH/IGF-1 (CJC-1295 indirectly via GHRH-R, HGH 191AA directly via GHR). Stacking provides no synergy and compounds GH-related side effects.",
		overlapWarningKey: "cjc1295_hgh191aa",
	},
	{
		peptides: ["cjc-1295", "kpv"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms with supportive overlap - CJC-1295's GHRH-R-driven GH/IGF-1 release raises the anabolic baseline for tissue repair, while KPV (alpha-MSH 11-13 fragment) provides melanocortin-pathway anti-inflammatory action via NF-kB suppression. A calmer inflammatory environment can support GH-driven recovery endpoints; no receptor overlap.",
		rationaleKey: "cjc1295_kpv",
		timingAdvice:
			"CJC-1295 daily evening before bed; KPV daily (oral or subcutaneous). No coordination needed.",
		timingAdviceKey: "cjc1295_kpv",
	},
	{
		peptides: ["cjc-1295", "mots-c"],
		synergyScore: 4,
		rationale:
			"Complementary body-composition mechanisms - CJC-1295's GHRH-R-mediated GH release supports lean mass and lipolysis, while MOTS-c enhances AMPK-driven fat oxidation and insulin sensitivity, helping offset GH-related insulin antagonism.",
		rationaleKey: "cjc1295_motsc",
		timingAdvice:
			"CJC-1295 daily evening; MOTS-c once weekly. No coordination needed; monitor fasting glucose.",
		timingAdviceKey: "cjc1295_motsc",
	},
	{
		peptides: ["cjc-1295", "mt-2"],
		synergyScore: 2,
		rationale:
			"Orthogonal receptor systems - CJC-1295 acts on GHRH-R to stimulate GH release, while MT-2 acts on melanocortin receptors (MC1R/MC3R/MC4R) for tanning. No known synergy or antagonism.",
		rationaleKey: "cjc1295_mt2",
		timingAdvice:
			"CJC-1295 evening before bed; MT-2 evening with food. Space dosing 30+ minutes apart. No interaction.",
		timingAdviceKey: "cjc1295_mt2",
	},
	{
		peptides: ["cjc-1295", "nad-plus"],
		synergyScore: 4,
		rationale:
			"Strong systemic anti-aging pairing - CJC-1295's pulsatile GH/IGF-1 stimulation drives anabolic remodeling and lipolysis, while NAD+ fuels SIRT1/SIRT3 mitochondrial biogenesis and PARP-mediated DNA repair. Endocrine and energetic axes align.",
		rationaleKey: "cjc1295_nadplus",
		timingAdvice: "NAD+ daily morning for circadian alignment; CJC-1295 evening before bed. No interaction.",
		timingAdviceKey: "cjc1295_nadplus",
	},
	{
		peptides: ["cjc-1295", "retatrutide"],
		synergyScore: 3,
		rationale:
			"Independent metabolic pathways - CJC-1295's GHRH-R-driven GH/IGF-1 release supports lean-mass preservation and lipolysis, while Retatrutide's GLP-1R/GIPR/GCGR triple-agonism drives appetite suppression and energy expenditure. GH can blunt insulin sensitivity, partially offset by Retatrutide's incretin effects; monitor fasting glucose.",
		rationaleKey: "cjc1295_retatrutide",
		timingAdvice: "Retatrutide once weekly; CJC-1295 daily evening. No timing coordination required.",
		timingAdviceKey: "cjc1295_retatrutide",
	},
	{
		peptides: ["cjc-1295", "selank"],
		synergyScore: 2,
		rationale:
			"Orthogonal pharmacology - endocrine GHRH-R/GH signaling versus Selank's allosteric GABA-A anxiolytic modulation and BDNF/NGF support. No known interaction.",
		rationaleKey: "cjc1295_selank",
		timingAdvice: "Selank intranasal morning; CJC-1295 evening before bed. No interaction.",
		timingAdviceKey: "cjc1295_selank",
	},
	{
		peptides: ["cjc-1295", "semax"],
		synergyScore: 2,
		rationale:
			"Independent endocrine versus CNS pathways - CJC-1295's GHRH-R-mediated GH release versus Semax's melanocortin/BDNF/TrkB-driven neurotrophic effects. No known interaction.",
		rationaleKey: "cjc1295_semax",
		timingAdvice: "Semax intranasal morning; CJC-1295 evening before bed. No interaction.",
		timingAdviceKey: "cjc1295_semax",
	},
	{
		peptides: ["cjc-1295", "tb500"],
		synergyScore: 3,
		rationale:
			"GH-axis complement - CJC-1295's GHRH-R-mediated pulsatile GH release elevates the systemic anabolic environment that supports TB-500's G-actin-driven cell migration and VEGF-mediated angiogenesis. No receptor overlap.",
		rationaleKey: "cjc1295_tb500",
		timingAdvice: "TB-500 2-3x weekly; CJC-1295 daily evening before bed. No coordination needed.",
		timingAdviceKey: "cjc1295_tb500",
	},
	{
		peptides: ["cjc-1295", "tesamorelin"],
		synergyScore: 1,
		rationale:
			"Mechanistic redundancy - both are GHRH-R agonists that stimulate pulsatile endogenous GH release from the anterior pituitary. Co-administration competes for the same receptor pool with no additive benefit and an amplified GH-related side-effect profile (insulin resistance, edema, carpal-tunnel risk). Not recommended.",
		rationaleKey: "cjc1295_tesamorelin",
		timingAdvice:
			"Choose one GHRH analog, not both. If transitioning, allow a 1-2 week washout before starting the other.",
		timingAdviceKey: "cjc1295_tesamorelin",
		overlapWarning:
			"Critical receptor redundancy - CJC-1295 and Tesamorelin are both GHRH-R agonists. Stacking provides no synergy and compounds GH-related side effects.",
		overlapWarningKey: "cjc1295_tesamorelin",
	},
	{
		peptides: ["cjc-1295", "thymosin-alpha-1"],
		synergyScore: 3,
		rationale:
			"Independent endocrine and immune pathways - CJC-1295's GHRH-R-mediated GH/IGF-1 release supports anabolism and recovery, while Thymosin Alpha-1 modulates T-cell maturation and TLR-mediated innate immunity. No receptor overlap; GH itself has thymotrophic effects, so a restorative immune context can complement GH-axis recovery research.",
		rationaleKey: "cjc1295_thymosinalpha1",
		timingAdvice:
			"CJC-1295 daily evening before bed; Thymosin Alpha-1 1-2x weekly subcutaneous. No coordination needed.",
		timingAdviceKey: "cjc1295_thymosinalpha1",
	},
	{
		peptides: ["cjc-1295", "tirzepatid"],
		synergyScore: 3,
		rationale:
			"Complementary body composition mechanisms - CJC-1295's GHRH-R-mediated GH release supports lean mass and lipolysis, while Tirzepatid's GLP-1R/GIPR dual-agonism suppresses appetite and improves insulin sensitivity. GH-induced insulin antagonism is partially offset by Tirzepatid; monitor fasting glucose.",
		rationaleKey: "cjc1295_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; CJC-1295 daily evening. No timing coordination required.",
		timingAdviceKey: "cjc1295_tirzepatid",
	},
	{
		peptides: ["ghk-cu", "kpv"],
		synergyScore: 5,
		rationale:
			"Excellent skin and barrier combination - GHK-Cu drives collagen/elastin synthesis and ECM remodeling via copper-mediated gene activation and suppresses IL-6/TNF-α, while KPV adds receptor-independent NF-κB and MAPK inhibition for potent anti-inflammatory and antimicrobial action. Complementary tissue-building and inflammation-resolving mechanisms widely paired for skin and wound research.",
		rationaleKey: "ghkcu_kpv",
		timingAdvice:
			"Both flexible. GHK-Cu daily (subcutaneous or topical); KPV daily (oral, subcutaneous, or topical). No coordination needed.",
		timingAdviceKey: "ghkcu_kpv",
	},
	{
		peptides: ["ghk-cu", "thymosin-alpha-1"],
		synergyScore: 4,
		rationale:
			"Complementary anti-aging-plus-immune pairing — GHK-Cu remodels the extracellular matrix and activates antioxidant-defense genes (SOD, glutathione) via copper-mediated gene activation, while Thymosin Alpha-1 strengthens T-cell-driven immunity through TLR2/TLR9 signaling. Parallel tissue-level and systemic immune effects with no receptor overlap.",
		rationaleKey: "ghkcu_thymosinalpha1",
		timingAdvice:
			"GHK-Cu daily (subcutaneous or topical); Thymosin Alpha-1 2-3x weekly. No coordination needed.",
		timingAdviceKey: "ghkcu_thymosinalpha1",
	},
	{
		peptides: ["hgh-191aa", "kpv"],
		synergyScore: 4,
		rationale:
			"Anabolic-plus-anti-inflammatory pairing - HGH 191AA drives IGF-1-mediated protein synthesis and lean-mass support via GHR/JAK2-STAT5, while KPV's receptor-independent NF-κB inhibition reduces inflammatory load to support recovery. Independent receptor systems, complementary healing profile.",
		rationaleKey: "hgh191aa_kpv",
		timingAdvice:
			"HGH 191AA evening before bed; KPV flexible (oral or subcutaneous, any time). No coordination needed.",
		timingAdviceKey: "hgh191aa_kpv",
	},
	{
		peptides: ["hgh-191aa", "thymosin-alpha-1"],
		synergyScore: 4,
		rationale:
			"Anabolic-plus-immune pairing — HGH 191AA drives systemic anabolism via GHR/IGF-1 and JAK2-STAT5 signaling, while Thymosin Alpha-1 supports T-cell maturation and immune resilience via TLR2/TLR9. Independent receptor systems; immune support complements the recovery demands of an anabolic protocol.",
		rationaleKey: "hgh191aa_thymosinalpha1",
		timingAdvice:
			"HGH 191AA daily evening before bed; Thymosin Alpha-1 2-3x weekly. No timing coordination required.",
		timingAdviceKey: "hgh191aa_thymosinalpha1",
	},
	{
		peptides: ["kpv", "mots-c"],
		synergyScore: 4,
		rationale:
			"Complementary metabolic-plus-anti-inflammatory pairing - MOTS-c activates AMPK and translocates to the nucleus to co-regulate NRF2 antioxidant and NF-κB anti-inflammatory gene programs, while KPV independently inhibits NF-κB nuclear translocation and stabilizes IκB-α. Both converge on NF-κB suppression from different mechanisms, pairing metabolic flexibility with inflammation resolution.",
		rationaleKey: "kpv_motsc",
		timingAdvice: "MOTS-c once weekly morning; KPV daily (oral or subcutaneous). No coordination needed.",
		timingAdviceKey: "kpv_motsc",
	},
	{
		peptides: ["kpv", "mt-2"],
		synergyScore: 3,
		rationale:
			"Orthogonal melanocortin pathways with no competition - MT-2 is a non-selective MC1R/MC3R/MC4R agonist driving melanogenesis, while KPV (the C-terminal tripeptide of alpha-MSH) acts receptor-independently via intracellular NF-κB inhibition and does NOT trigger MC1R pigmentation. Despite the shared alpha-MSH lineage there is no receptor overlap or competition.",
		rationaleKey: "kpv_mt2",
		timingAdvice: "MT-2 evening; KPV flexible (oral or subcutaneous, any time). No interaction.",
		timingAdviceKey: "kpv_mt2",
	},
	{
		peptides: ["kpv", "nad-plus"],
		synergyScore: 4,
		rationale:
			"Strong anti-aging and cellular-resilience pairing - NAD+ fuels sirtuin (SIRT1/SIRT3) longevity signaling and PARP-mediated DNA repair, while KPV provides receptor-independent NF-κB inhibition; sirtuins and NF-κB are reciprocally linked, so SIRT-driven deacetylation and KPV's IκB-α stabilization converge to dampen inflammatory gene programs. Complementary cellular and anti-inflammatory mechanisms.",
		rationaleKey: "kpv_nadplus",
		timingAdvice:
			"NAD+ daily morning for circadian alignment; KPV flexible (oral or subcutaneous). No coordination needed.",
		timingAdviceKey: "kpv_nadplus",
	},
	{
		peptides: ["kpv", "retatrutide"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms with supportive overlap - Retatrutide's GLP-1R/GIPR/GCGR triple-agonism drives metabolic remodeling, while KPV's intracellular NF-κB inhibition may help dampen low-grade inflammation associated with rapid weight change and protect GI tissue during incretin-related adaptation. No receptor overlap.",
		rationaleKey: "kpv_retatrutide",
		timingAdvice: "Retatrutide once weekly; KPV daily (oral or subcutaneous). No coordination needed.",
		timingAdviceKey: "kpv_retatrutide",
	},
	{
		peptides: ["kpv", "selank"],
		synergyScore: 2,
		rationale:
			"Orthogonal systems - Selank provides allosteric GABA-A modulation and BDNF/NGF neuromodulation for calm-focus, while KPV delivers receptor-independent anti-inflammatory action via NF-κB and MAPK inhibition. No known synergy or shared endpoints.",
		rationaleKey: "kpv_selank",
		timingAdvice: "Selank intranasal morning; KPV flexible (oral or subcutaneous). No interaction.",
		timingAdviceKey: "kpv_selank",
	},
	{
		peptides: ["kpv", "semax"],
		synergyScore: 2,
		rationale:
			"Largely independent pathways - Semax drives melanocortin MC3R/MC4R and BDNF/TrkB neurotrophic signaling for cognition, while KPV acts receptor-independently via intracellular NF-κB inhibition for peripheral anti-inflammatory and gut/skin endpoints. No meaningful synergy and no shared research endpoints.",
		rationaleKey: "kpv_semax",
		timingAdvice: "Semax intranasal morning; KPV flexible (oral or subcutaneous). No interaction.",
		timingAdviceKey: "kpv_semax",
	},
	{
		peptides: ["kpv", "tb500"],
		synergyScore: 5,
		rationale:
			"Excellent recovery combination - TB-500 promotes cell migration via actin polymerization and angiogenesis, while KPV provides receptor-independent anti-inflammatory action through NF-κB and MAPK inhibition. Both modulate NF-κB anti-inflammatory signaling from complementary directions, supporting tissue repair with reduced inflammatory load.",
		rationaleKey: "kpv_tb500",
		timingAdvice:
			"Both flexible. TB-500 2-3x weekly; KPV daily (oral or subcutaneous). No coordination needed.",
		timingAdviceKey: "kpv_tb500",
	},
	{
		peptides: ["kpv", "tesamorelin"],
		synergyScore: 3,
		rationale:
			"GH-axis complement - Tesamorelin's pulsatile GH release via GHRH-R elevates the anabolic and tissue-remodeling environment, while KPV's intracellular NF-κB inhibition reduces inflammatory load to support healing. No receptor overlap.",
		rationaleKey: "kpv_tesamorelin",
		timingAdvice:
			"Tesamorelin evening before bed; KPV flexible (oral or subcutaneous, any time). No coordination needed.",
		timingAdviceKey: "kpv_tesamorelin",
	},
	{
		peptides: ["kpv", "thymosin-alpha-1"],
		synergyScore: 4,
		rationale:
			"Complementary immune-modulation pairing - Thymosin alpha-1 promotes T-cell maturation and dendritic-cell activation via TLR-2/TLR-9 signaling to up-regulate immune surveillance, while KPV exerts receptor-independent anti-inflammatory action by inhibiting NF-κB and MAPK. The two balance immune activation against inflammatory restraint, useful in gut and infection-resolution research where both immune competence and inflammation control are endpoints.",
		rationaleKey: "kpv_thymosinalpha1",
		timingAdvice:
			"Both flexible. Thymosin alpha-1 2-3x weekly subcutaneous; KPV daily (oral or subcutaneous). No coordination needed.",
		timingAdviceKey: "kpv_thymosinalpha1",
	},
	{
		peptides: ["kpv", "tirzepatid"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms with supportive overlap - Tirzepatid's GLP-1R/GIPR dual-agonism suppresses appetite and improves insulin sensitivity, while KPV's receptor-independent NF-κB and MAPK inhibition may attenuate metabolic inflammation and support GI mucosal integrity during incretin adaptation. No receptor overlap.",
		rationaleKey: "kpv_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; KPV daily (oral or subcutaneous). No coordination needed.",
		timingAdviceKey: "kpv_tirzepatid",
	},
	{
		peptides: ["mots-c", "thymosin-alpha-1"],
		synergyScore: 3,
		rationale:
			"Independent mechanisms with supportive overlap — Thymosin Alpha-1 drives TLR2/TLR9-mediated immune modulation, while MOTS-c enhances AMPK-driven metabolic flexibility and NRF2 antioxidant signaling. No receptor overlap; a healthier metabolic and antioxidant baseline can support immune and recovery research endpoints.",
		rationaleKey: "motsc_thymosinalpha1",
		timingAdvice: "MOTS-c once weekly; Thymosin Alpha-1 2-3x weekly. No coordination needed.",
		timingAdviceKey: "motsc_thymosinalpha1",
	},
	{
		peptides: ["mt-2", "thymosin-alpha-1"],
		synergyScore: 2,
		rationale:
			"Orthogonal receptor systems — Thymosin Alpha-1 acts on the TLR2/TLR9 immune axis, while MT-2 is a non-selective melanocortin (MC1R/MC3R/MC4R) agonist for tanning. No known synergy or antagonism.",
		rationaleKey: "mt2_thymosinalpha1",
		timingAdvice: "Thymosin Alpha-1 2-3x weekly (any time); MT-2 evening. No interaction.",
		timingAdviceKey: "mt2_thymosinalpha1",
	},
	{
		peptides: ["nad-plus", "thymosin-alpha-1"],
		synergyScore: 4,
		rationale:
			"Strong anti-aging combination — Thymosin Alpha-1 drives TLR2/TLR9-mediated immune resilience and T-cell function, while NAD+ fuels sirtuin-driven (SIRT1/SIRT3) longevity gene expression, PARP-mediated DNA repair, and mitochondrial Complex I capacity. Immune fitness and cellular energy substrate target aging in parallel.",
		rationaleKey: "nadplus_thymosinalpha1",
		timingAdvice:
			"NAD+ daily morning for circadian alignment; Thymosin Alpha-1 2-3x weekly. No coordination needed.",
		timingAdviceKey: "nadplus_thymosinalpha1",
	},
	{
		peptides: ["retatrutide", "thymosin-alpha-1"],
		synergyScore: 3,
		rationale:
			"Independent pharmacology — Thymosin Alpha-1's TLR2/TLR9-driven immune modulation versus Retatrutide's GLP-1R/GIPR/GCGR triple-agonist metabolic remodeling. No receptor overlap; immune support may complement the physiological stress of rapid body composition change.",
		rationaleKey: "retatrutide_thymosinalpha1",
		timingAdvice: "Retatrutide once weekly; Thymosin Alpha-1 2-3x weekly. No coordination needed.",
		timingAdviceKey: "retatrutide_thymosinalpha1",
	},
	{
		peptides: ["selank", "thymosin-alpha-1"],
		synergyScore: 2,
		rationale:
			"Orthogonal systems — Thymosin Alpha-1's TLR2/TLR9-driven immune modulation versus Selank's allosteric GABA-A modulation and BDNF/NGF neurotrophic support. No known synergy or interaction.",
		rationaleKey: "selank_thymosinalpha1",
		timingAdvice: "Selank intranasal morning; Thymosin Alpha-1 2-3x weekly subcutaneous. No interaction.",
		timingAdviceKey: "selank_thymosinalpha1",
	},
	{
		peptides: ["semax", "thymosin-alpha-1"],
		synergyScore: 2,
		rationale:
			"Independent pharmacology — Thymosin Alpha-1's TLR2/TLR9-driven immune modulation versus Semax's melanocortin (MC3R/MC4R) and BDNF/TrkB-driven neurotrophic effects. No receptor overlap and no known interaction.",
		rationaleKey: "semax_thymosinalpha1",
		timingAdvice: "Semax intranasal morning; Thymosin Alpha-1 2-3x weekly subcutaneous. No interaction.",
		timingAdviceKey: "semax_thymosinalpha1",
	},
	{
		peptides: ["tb500", "thymosin-alpha-1"],
		synergyScore: 4,
		rationale:
			"Same thymosin family but mechanistically distinct — TB-500 (Thymosin Beta-4 fragment) drives actin polymerization and cell migration for tissue repair, while Thymosin Alpha-1 modulates immunity via TLR2/TLR9-driven T-cell maturation. No receptor overlap despite shared family nomenclature; complementary recovery-plus-immune profile.",
		rationaleKey: "tb500_thymosinalpha1",
		timingAdvice:
			"Both flexible. TB-500 2-3x weekly; Thymosin Alpha-1 2-3x weekly, separate injection sites. No coordination needed.",
		timingAdviceKey: "tb500_thymosinalpha1",
	},
	{
		peptides: ["tesamorelin", "thymosin-alpha-1"],
		synergyScore: 3,
		rationale:
			"Independent endocrine versus immune pathways — Tesamorelin's GHRH-R agonism stimulates pulsatile GH release and reduces visceral fat, while Thymosin Alpha-1 drives TLR2/TLR9-mediated T-cell maturation. No receptor overlap; GH-axis support and immune resilience are complementary anti-aging endpoints.",
		rationaleKey: "tesamorelin_thymosinalpha1",
		timingAdvice:
			"Tesamorelin daily evening before bed; Thymosin Alpha-1 2-3x weekly. No coordination needed.",
		timingAdviceKey: "tesamorelin_thymosinalpha1",
	},
	{
		peptides: ["thymosin-alpha-1", "tirzepatid"],
		synergyScore: 3,
		rationale:
			"Independent pharmacology — Thymosin Alpha-1's TLR2/TLR9-driven T-cell modulation versus Tirzepatid's GLP-1R/GIPR dual-agonist appetite suppression and insulin sensitization. No receptor overlap; immune support is useful alongside metabolic adaptation.",
		rationaleKey: "thymosinalpha1_tirzepatid",
		timingAdvice: "Tirzepatid once weekly; Thymosin Alpha-1 2-3x weekly. No coordination needed.",
		timingAdviceKey: "thymosinalpha1_tirzepatid",
	},
];

export const GOAL_STACKS: GoalStack[] = [
	{
		goal: "recovery",
		nameKey: "recovery",
		descriptionKey: "recovery",
		peptides: ["bpc-157", "tb500"],
		icon: "shield",
	},
	{
		goal: "anti-aging",
		nameKey: "antiAging",
		descriptionKey: "antiAging",
		peptides: ["bpc-157", "tb500"],
		icon: "sparkles",
	},
	{
		goal: "weight-management",
		nameKey: "weightManagement",
		descriptionKey: "weightManagement",
		peptides: ["retatrutide"],
		icon: "scale",
	},
	{
		goal: "skin",
		nameKey: "skin",
		descriptionKey: "skin",
		peptides: ["bpc-157", "ghk-cu", "tb500"],
		icon: "droplets",
	},
	{
		goal: "tanning",
		nameKey: "tanning",
		descriptionKey: "tanning",
		peptides: ["mt-2"],
		icon: "sun",
	},
	{
		goal: "cognitive",
		nameKey: "cognitive",
		descriptionKey: "cognitive",
		peptides: ["semax", "selank"],
		icon: "brain",
	},
];

/**
 * Allow-list for the public `?goal=` param, derived from GOAL_STACKS so it cannot drift.
 *
 * The public slugs are kebab-case ("weight-management") while the i18n keys under
 * `stackBuilder.goals` are camelCase ("weightManagement"). GOAL_STACKS carries both, and both
 * are accepted: the kebab slug is what the builder writes into share URLs, the camelCase
 * nameKey appears in links that are already in the wild.
 *
 * A Map (not a plain object) so `__proto__` / `constructor` are ordinary misses.
 */
const GOAL_STACK_BY_PARAM: ReadonlyMap<string, GoalStack> = new Map(
	GOAL_STACKS.flatMap((stack) => [[stack.goal, stack] as const, [stack.nameKey, stack] as const]),
);

/**
 * Resolve an arbitrary `?goal=` value to its GoalStack, or null when it is not a known goal.
 * Every surface that reads the param (metadata, client state, OG/share URLs) must go through
 * this so an unvalidated value can never reach a message key or the OG renderer.
 */
export function resolveGoalStack(goal: string | null | undefined): GoalStack | null {
	if (!goal) return null;
	return GOAL_STACK_BY_PARAM.get(goal) ?? null;
}

/** Canonical kebab-case `?goal=` slug for an arbitrary param value; null when unknown. */
export function resolveGoalSlug(goal: string | null | undefined): PeptideGoal | null {
	return resolveGoalStack(goal)?.goal ?? null;
}

export const BLEND_MATCHES: BlendMatch[] = [
	{ individual: ["bpc-157", "tb500"], blend: "tb500-bpc157-blend" },
	{ individual: ["bpc-157", "ghk-cu", "tb500"], blend: "glow-stack" },
	{ individual: ["bpc-157", "ghk-cu", "kpv", "tb500"], blend: "klow-stack" },
];

/**
 * Map any peptide slug (stack-profile, calculator/peptide-data, or Saleor product slug)
 * to the Stack-Builder PROFILE slug so it survives the builder's `?s=` validation against
 * PEPTIDE_PROFILES (e.g. spoke "tb-500" → profile "tb500", calculator "melanotan-2" →
 * profile "mt-2"). Returns null when the peptide is not modelled in the builder — callers
 * then link to the plain builder instead of appending a dead `?s=`.
 */
export function toStackProfileSlug(slug: string): string | null {
	if (PEPTIDE_PROFILES.some((p) => p.slug === slug)) return slug;
	for (const [key, link] of Object.entries(PEPTIDE_LINKS)) {
		if (
			(link.calculatorSlug === slug || link.productSlug === slug) &&
			PEPTIDE_PROFILES.some((p) => p.slug === key)
		) {
			return key;
		}
	}
	return null;
}
